import React, { useEffect, useState } from 'react';
import {
  getStudentProfileDetail,
  getSsaDashboardPassword,
  resetSsaDashboardPassword,
} from './service/ssaProfileApi';
import MDBox from 'components/MDBox';
import {
  SsaProfileLabel,
  SsaProfileHeaderLabel,
} from './service/ssaProfileTabUtils';
import Grid from '@mui/material/Grid';
import {
  SsaPasswordBox,
  SsaProfileButton,
  PasswordTypography,
} from './service/SsaProfileTabStyled';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import MDTypography from 'components/MDTypography';
import Visibility from '@mui/icons-material/Visibility';
import { AccordianContainer } from 'components/Ui/styled';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { SsaStudentProfileLoader } from '../../ssaCommonUtils';
import { useNotification, handleBooleanProp } from 'utils/commonUtils';
import ConfirmationDialog from 'components/Common/ConfirmationDialog/ConfirmationDialog';
import { usePermissionCustomHook } from 'layouts/users/userPermissions/utils/permissionsUtils';
import { AcademinProgressGrid } from '../SsaAcademicProgressTabComponent/SsaAcademicProgressTabStyled';
import StudentProfileNote from 'layouts/studentManagement/components/studentProfile/components/studentProfileTabs/StudentProfileNote';

const SsaProfileTab = () => {
  const canViewStudentPassword = usePermissionCustomHook(
    'CAN_VIEW_STUDENT_DASHBOARD_PASSWORDS',
  );
  const canResetStudentPassword = usePermissionCustomHook(
    'CAN_RESET_STUDENT_DASHBOARD_PASSWORDS',
  );
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const ssaStudentDetail = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentHeadingDetails || {},
  );
  const { studentId } = ssaStudentDetail || {};

  const [password, setPassword] = useState({
    dashboardPassword: '',
    error: false,
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [profileDetails, setProfileDetails] = useState([]);
  const [resetLoadingButton, setResetLoadingButton] = useState(false);
  const [passwordBoxVisible, setPasswordBoxVisible] = useState(false);
  const [profileDetailsLoading, setProfileDetailsLoading] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const {
    note,
    tags,
    gender,
    stateId,
    lastName,
    schoolId,
    firstName,
    resourceId,
    iepTeacher,
    gradeLevel,
    studentType,
    dateOfBirth,
    raceEthnicity,
    homePrimaryLanguage,
  } = profileDetails?.profile || {};

  const useFunction = useForm({
    mode: 'onChange',
  });

  const { handleSubmit } = useFunction;

  useEffect(() => {
    fetchStudentProfileDetails();
    if (canViewStudentPassword) {
      fetchDashbardPassword();
    }
  }, []);

  const fetchStudentProfileDetails = async () => {
    try {
      setProfileDetailsLoading(true);
      const getProfileDetail = await getStudentProfileDetail(studentId);
      setProfileDetails(getProfileDetail);
      setProfileDetailsLoading(false);
    } catch (error) {
      setProfileDetailsLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchStudentNote = async () => {
    try {
      const getProfileDetail = await getStudentProfileDetail(studentId);
      setProfileDetails(getProfileDetail);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  if (profileDetailsLoading) {
    return <SsaStudentProfileLoader />;
  }

  const fetchDashbardPassword = async () => {
    try {
      const getPassword = await getSsaDashboardPassword(studentId);

      setPassword({
        dashboardPassword: getPassword?.dashboardPassword,
        error: false,
      });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message || 'Failed to load password';
      handleErrorResponse(errorMsg);

      setPassword({
        dashboardPassword: errorMsg,
        error: true,
      });
    }
  };

  const onSubmit = async () => {
    try {
      setResetLoadingButton(true);
      await resetSsaDashboardPassword(studentId);
      await fetchDashbardPassword();
      setResetLoadingButton(false);
      setOpenConfirmationDialog(false);
    } catch (error) {
      setResetLoadingButton(false);
      setOpenConfirmationDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleResetPassword = () => {
    setOpenConfirmationDialog(true);
  };

  const passwordText = passwordBoxVisible ? 'Hide Password' : 'View Password';
  const passwordIcon = passwordBoxVisible ? <VisibilityOff /> : <Visibility />;

  const handleAccordionChange = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <AcademinProgressGrid container display="flex" spacing={4}>
        <Grid item xs={12} md={5}>
          <SsaProfileHeaderLabel label="Basic Information" />
          <MDBox mb={1} />
          <SsaProfileLabel label="Name" value={`${firstName} ${lastName}`} />
          <SsaProfileLabel label="Student ID" value={resourceId} />
          <SsaProfileLabel label="Grade Level" value={gradeLevel} />
          <SsaProfileLabel label="Student Type" value={studentType} />
          <SsaProfileLabel label="School" value={schoolId} />
          <SsaProfileLabel label="State ID" value={stateId} />
          <SsaProfileLabel label="Tags" value={tags?.join(', ')} />
          <SsaProfileLabel
            label="IEP Teacher"
            value={iepTeacher || 'Not assigned'}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <SsaProfileHeaderLabel label="Demographics" />
          <MDBox mb={1} />
          <SsaProfileLabel label="Gender" value={gender} />
          <SsaProfileLabel label="Date of Birth" value={dateOfBirth} />
          <SsaProfileLabel label="Race/Ethnicity" value={raceEthnicity} />
          <SsaProfileLabel label="Home Language" value={homePrimaryLanguage} />
        </Grid>

        {/* Dashboard Password Section */}
        {canViewStudentPassword && (
          <Grid item xs={12}>
            <MDBox sx={{ mt: 3 }}>
              <SsaProfileHeaderLabel label="Dashboard Password" />
              <MDBox mb={3} />
              {/* Password Action Buttons */}

              <MDBox sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {/* View / Hide Toggle Button */}
                <SsaProfileButton
                  variant="outlined"
                  startIcon={passwordIcon}
                  onClick={() => setPasswordBoxVisible(!passwordBoxVisible)}
                >
                  {passwordText}
                </SsaProfileButton>

                {/* Reset Password Button */}
                {canResetStudentPassword && (
                  <SsaProfileButton
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleResetPassword}
                  >
                    Reset Password
                  </SsaProfileButton>
                )}
              </MDBox>

              {/* Password Display */}
              {passwordBoxVisible && (
                <SsaPasswordBox>
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MDTypography variant="body2" fontWeight="medium">
                      🔑 Dashboard Password:
                    </MDTypography>
                    <PasswordTypography
                      variant="body2"
                      fontWeight="bold"
                      error={handleBooleanProp(password?.error)}
                    >
                      {password?.dashboardPassword}
                    </PasswordTypography>
                  </MDBox>
                </SsaPasswordBox>
              )}
            </MDBox>
          </Grid>
        )}

        <Grid item xs={12} md={10}>
          <AccordianContainer
            expanded={isExpanded}
            defaultExpanded={false}
            onChange={handleAccordionChange}
          >
            <AccordionSummary
              id="panel1a-header"
              aria-controls="panel1a-content"
              expandIcon={<ExpandMoreIcon />}
            >
              <Typography variant="d7">Note</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <StudentProfileNote
                note={note}
                resourceId={resourceId}
                fetchStudentNote={fetchStudentNote}
              />
            </AccordionDetails>
          </AccordianContainer>
        </Grid>
      </AcademinProgressGrid>
      <ConfirmationDialog
        isSubmit={true}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        loadingButton={resetLoadingButton}
        openConfirmationDialog={openConfirmationDialog}
        setOpenConfirmationDialog={setOpenConfirmationDialog}
        title="Are you sure you want to reset dashboard password ?"
      />
      <NotificationPopup />
    </>
  );
};

export default SsaProfileTab;
